import { useCallback } from 'react';
import { TimelineResponseData } from 'framework/ui/timeline';
import { FilterCondition } from 'framework/types';
import { useRiskById } from './useRisks';
import { RISK_NOTE_SOURCE, RISK_SYSTEM_NOTE_SOURCE } from '../timeline/useRiskNotesRegistry';
import {
  RISK_TIMELINE_TYPE,
  RiskTimelineItem,
  TimelineCreateRequest,
  TimelineFilterRequest,
  UseRiskTimelineResult,
} from 'domain/shared/newProject';
import useRiskNotesService from '../useRiskNotesService';

const timelineItemToEntity = (item: RiskTimelineItem) => {
  const isSystemNote = item.type === RISK_TIMELINE_TYPE.SYSTEM_NOTE;

  return {
    id: item.id,
    timelineId: item.id,
    createdAt: item.createdAt,
    createdBy: item.createdBy,
    type: item.type,
    isPinned: item.isPinned,
    ...(isSystemNote
      ? {
          [RISK_SYSTEM_NOTE_SOURCE]: {
            ...item,
            payload: {
              text: item.text,
            },
          },
        }
      : {
          [RISK_NOTE_SOURCE]: {
            ...item,
            payload: {
              text: item.text,
              attachmentPaths: (item as any).attachmentPaths ?? [],
            },
          },
        }),
  };
};

const timelineToResponse = (items: RiskTimelineItem[] = []): TimelineResponseData => ({
  result: items.map(timelineItemToEntity) as unknown as TimelineResponseData extends { result: infer R } ? R : never,
  pageNumber: 0,
  totalPages: 1,
  totalElements: items.length,
});

export const useRiskTimeline = (projectId: string | number, riskId: string): UseRiskTimelineResult => {
  const { risk, projectName, permissions, loading, error, refetch } = useRiskById(projectId, riskId);
  const { createRiskNote, pinRiskTimeline } = useRiskNotesService();

  const getTimelineItems = useCallback(
    (_entityId: string, request: TimelineFilterRequest) => {
      const typeFilter: string[] | undefined = request?.filter?.type?.[FilterCondition.IN];
      const allItems: RiskTimelineItem[] = risk?.timeline ?? [];
      const filtered = typeFilter?.length
        ? allItems.filter(item => {
            const itemType = item.type ?? RISK_TIMELINE_TYPE.USER_NOTE;
            return typeFilter.includes(itemType);
          })
        : allItems;
      return Promise.resolve(timelineToResponse(filtered));
    },
    [risk?.timeline],
  );

  const getTimelinePageById = useCallback(
    (_entityId: string, _entryId: string) => {
      const allItems: RiskTimelineItem[] = risk?.timeline ?? [];
      return Promise.resolve(timelineToResponse(allItems));
    },
    [risk?.timeline],
  );

  const createTimelineItem = useCallback(
    async (riskIdParam: string, _itemType: string, request: TimelineCreateRequest) => {
      const created = await createRiskNote(riskIdParam, {
        text: request.text,
        attachmentPaths: request.attachmentPaths ?? [],
      });
      await refetch();
      return { ...created, timelineId: created.id };
    },
    [createRiskNote, refetch],
  );

  const pinTimelineAction = useCallback(
    async (_entityId: StringOrNumber, timelineId: StringOrNumber, pinStatus: boolean) => {
      const response = await pinRiskTimeline(projectId.toString(), riskId, timelineId.toString(), pinStatus);
      await refetch();
      return response;
    },
    [pinRiskTimeline, projectId, riskId, refetch],
  );

  return {
    data: risk,
    projectName,
    permissions,
    loading,
    error,
    refetch,
    getTimelineItems,
    getTimelinePageById,
    createTimelineItem,
    pinTimelineAction,
  };
};
